import { CSSProperties, FC, ReactNode } from 'react';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import SmallTooltip from '@/shared/components/tooltips/SmallTooltip';
import { useUserStore } from '@/stores';
import { formatDate, formatPreferredTimezoneDateTime } from '@/utilities';

interface ResourceFooterTimestampProps {
    date?: Date | string;
    icon: ReactNode;
    label: string;
    placement?: 'top' | 'topLeft' | 'topRight';
    style?: CSSProperties;
}

const ResourceFooterTimestamp: FC<ResourceFooterTimestampProps> = ({
    date,
    icon,
    label,
    placement = 'top',
    style,
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const preferredTimezone = useUserStore((state) => state.selectedUser?.timeZone);
    const parsedDate = date ? new Date(date) : undefined;
    const validDate = parsedDate && !Number.isNaN(parsedDate.getTime()) ? parsedDate : undefined;
    const tooltipTitle = validDate ? formatPreferredTimezoneDateTime(validDate, preferredTimezone) : undefined;

    return (
        <SmallTooltip placement={placement} showInfo={false} title={tooltipTitle}>
            <small
                data-resource-footer-timestamp={label.toLowerCase()}
                style={{
                    alignItems: 'center',
                    color: token.colorTextTertiary,
                    display: 'inline-flex',
                    lineHeight: token.lineHeightSM,
                    maxWidth: '100%',
                    minWidth: 0,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    ...style,
                }}
            >
                <span aria-hidden style={{ display: 'inline-flex', flex: 'none', marginRight: '0.25rem' }}>
                    {icon}
                </span>
                <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {label} {validDate ? formatDate(validDate) : '—'}
                </span>
            </small>
        </SmallTooltip>
    );
};

export type { ResourceFooterTimestampProps };
export default ResourceFooterTimestamp;
